import { useState } from "react"
import { Button, InfoCard, OtpInput } from "@cividao/components"
import styles from "./index.module.css"

export type VerificationCodeFormLiterals = {
  title: string
  subtitle: string
  codeLabel: string
  changeEmail: string
  resendCode: string
  resendSuccess: string
  button: string
  error: {
    incompleteCode: string
    invalidCode: string
    title: string
    message: string
  }
}

interface VerificationCodeFormProps {
  email: string
  literals: VerificationCodeFormLiterals
  onSubmit: (email: string, authCode: string) => Promise<void>
  onResend: () => Promise<void>
  onBack?: () => void
}

export function VerificationCodeForm({ email, literals, onSubmit, onResend, onBack }: VerificationCodeFormProps) {
  const [codeString, setCodeString] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState("")
  const [resent, setResent] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  const handleResend = () => {
    setIsResending(true)
    setError("")
    setResent(false)
    onResend()
      .then(() => {
        setResent(true)
      })
      .finally(() => {
        setIsResending(false)
      })
  }

  const handleSubmit = () => {
    if (codeString.length !== 6) return

    setIsLoading(true)

    onSubmit(email, codeString)
      .catch((err: Error) => {
        setError(err.message || literals.error.message)
        setCodeString("")
        setResetKey((k) => k + 1)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <div className={styles.form}>
      <p className={styles.subtitle} dangerouslySetInnerHTML={{ __html: literals.title }} />

      <OtpInput
        key={resetKey}
        label={literals.codeLabel}
        autoFocus
        isDisabled={isLoading}
        onChange={setCodeString}
        errorText={error || undefined}
      />

      {error && <InfoCard dismissable={false} variant="warning" title={literals.error.title} message={error} />}

      <Button onClick={handleSubmit} isDisabled={codeString.length !== 6} isLoading={isLoading} isFull>
        {literals.button}
      </Button>

      <Button type="outline" onClick={handleResend} isLoading={isResending} isDisabled={isLoading} isFull>
        {literals.resendCode}
      </Button>

      {resent && <InfoCard dismissable={false} variant="info" message={literals.resendSuccess} />}

      {onBack && (
        <Button type="text" onClick={onBack} isFull>
          {literals.changeEmail}
        </Button>
      )}
    </div>
  )
}
